/**
 * Client for the DI Deck AI search back-end (PRD §7.2).
 *
 * Sprint 4 ships a local stub that returns a canned response after a
 * simulated latency. The stub is keyword-routed against the tab
 * content so answers reference real content and cited sources link to
 * real tabs — the front-end is fully exercised end-to-end without a
 * real /api/ask endpoint.
 *
 * Sprint 5 replaces `askStub()` with a `fetch('/api/ask', …)` call to
 * a Cloudflare Worker that runs RAG over the curated corpus. The
 * exported types (AskRequest, AskResponse) are the wire contract both
 * sides commit to; the stub follows the same shape.
 */

import { TABS } from '../tabs/tabs-data';
import { FAQ } from '../data/pricing-data';

export interface AskRequest {
  q: string;
  source: string;
}

export interface AskSource {
  title: string;
  url: string;
  snippet: string;
}

export interface AskResponse {
  answer: string;
  sources: AskSource[];
  followUps: string[];
  stubbed?: boolean;
}

const MIN_LATENCY_MS = 900;
const MAX_LATENCY_MS = 1600;

export async function ask(req: AskRequest): Promise<AskResponse> {
  const latency = MIN_LATENCY_MS + Math.random() * (MAX_LATENCY_MS - MIN_LATENCY_MS);
  await new Promise((resolve) => setTimeout(resolve, latency));
  return askStub(req);
}

function askStub(req: AskRequest): AskResponse {
  const q = req.q.toLowerCase();

  const matchedFaq = FAQ.find((f) => tokensOverlap(q, f.q.toLowerCase()) >= 2);
  if (matchedFaq) {
    return {
      answer: matchedFaq.a,
      sources: pickTopSources(q, 3, req.source),
      followUps: [
        'How does DI Deck compare to a general-purpose AI gateway?',
        'What is included in the tailored pilot?',
        'Can I run DI Deck without any external egress?',
      ],
      stubbed: true,
    };
  }

  const matchedTab = TABS.find((t) => q.includes(t.title.toLowerCase()) || q.includes(t.slug));
  if (matchedTab) {
    return {
      answer:
        `The ${matchedTab.title} tab covers this in depth. ${matchedTab.promise} ` +
        `Full detail — including how-it-works and evidence points — is on that tab.`,
      sources: [
        {
          title: matchedTab.title,
          url: `/${matchedTab.slug}`,
          snippet: matchedTab.paragraphs[0] ?? matchedTab.promise,
        },
        ...pickTopSources(q, 2, req.source).filter((s) => s.url !== `/${matchedTab.slug}`),
      ],
      followUps: [
        `How does ${matchedTab.title} interact with the Sovereign Proxy?`,
        `Is ${matchedTab.title} opt-in or on by default?`,
        `What ledger entries does ${matchedTab.title} write?`,
      ],
      stubbed: true,
    };
  }

  return {
    answer:
      'I don\'t have a specific answer to that question in the public content yet. ' +
      'For a concrete answer, the fastest path is to email sales@dideck.com — most ' +
      'questions get a same-day reply. Meanwhile, the tabs below are the closest ' +
      'matches based on what you asked.',
    sources: pickTopSources(q, 3, req.source),
    followUps: [
      'Which regulated sectors are you piloting with today?',
      'Can DI Deck run air-gapped?',
      'How long does deployment take?',
    ],
    stubbed: true,
  };
}

/**
 * Score every tab against the query by counting token overlap between
 * the query and the tab's title + promise + first paragraph. Pick the
 * top N. If a specific tab-source is given, bias slightly toward that
 * tab so the answer stays contextually relevant.
 */
function pickTopSources(q: string, n: number, source: string): AskSource[] {
  const scored = TABS.map((t) => {
    const haystack = `${t.title} ${t.promise} ${t.paragraphs[0] ?? ''}`.toLowerCase();
    const score = tokensOverlap(q, haystack);
    const bias = source && `/${source}` === `/${t.slug}` ? 0.5 : 0;
    return { tab: t, score: score + bias };
  })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, n);

  if (scored.length === 0) {
    // Fall back to three reasonable defaults for a cold visitor query.
    return ['sovereign-proxy', 'ledger', 'eu-ai-act']
      .map((slug) => TABS.find((t) => t.slug === slug))
      .filter((t): t is (typeof TABS)[number] => t !== undefined)
      .map((t) => ({
        title: t.title,
        url: `/${t.slug}`,
        snippet: t.promise,
      }));
  }

  return scored.map(({ tab }) => ({
    title: tab.title,
    url: `/${tab.slug}`,
    snippet: tab.promise,
  }));
}

/** Very lightweight token-overlap count. Good enough for a stub. */
function tokensOverlap(a: string, b: string): number {
  const at = tokenize(a);
  const bt = new Set(tokenize(b));
  let n = 0;
  for (const t of at) {
    if (bt.has(t)) n++;
  }
  return n;
}

function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 3 && !STOPWORDS.has(w));
}

const STOPWORDS = new Set([
  'what',
  'when',
  'where',
  'which',
  'does',
  'work',
  'with',
  'have',
  'from',
  'this',
  'that',
  'they',
  'them',
  'your',
  'about',
  'into',
  'like',
  'will',
  'been',
  'would',
  'could',
  'should',
  'their',
  'there',
  'some',
  'more',
  'than',
  'then',
  'much',
  'over',
]);
